import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Observable, Subscription } from 'rxjs';
import { UrlQueryState } from '../url-query-state';
import { debounce } from 'lodash';

type Primitive = string | number | boolean;
/**
 * Get object that can use the query params in the url as a store for
 * state
 * @param defaults The default query params object.
 * @param mode The mode to use when updating history. Defaults to `replace`
 */
export function useQueryState<T extends Record<string, Primitive> = Record<string, string>>(
  defaults: T = {} as T,
  readFromUrl = true,
  mode: 'push' | 'replace' = 'replace'
): UrlQueryState<T> {
  const history = useHistory();
  const location = useLocation();
  const paramManager = useMemo(() => {
    const query = new UrlQueryState<T>(history, mode, location, defaults);
    return readFromUrl ? query.updateFromUrl() : query;
  }, []);

  // Merge query params and defaults.
  useEffect(() => {
    paramManager.updateFromUrl();
    return () => paramManager.cleanUp();
  }, []);

  return paramManager;
}

type SetTrue = () => any;
type SetFalse = () => any;
type Toggle = () => any;
export function useBoolean<T extends unknown = boolean>(
  def: T,
  trueValue = true as T,
  falseValue = false as T
) {
  const [value, setValue] = useState(def);
  const setFalse: SetFalse = useCallback(() => setValue(falseValue), [setValue]);
  const setTrue: SetTrue = useCallback(() => setValue(trueValue), [setValue]);
  return [value, setTrue, setFalse, setValue] as const;
}

/**
 * Create a callback that never changes (in current lifecycle), and returns the
 * latest `data`.
 * @param data The data to create getter for,
 */
export function useRecentGetter<T>(data: T) {
  const value = useRef<T>(data);
  value.current = data;
  return useCallback(() => value.current, [value]);
}

/**
 * Create a never changing callback that memonizes the
 * latest callback given.
 * @param cb The callback to memonize.
 */
export function useRecentCallback<T extends (...args: K) => any, K extends any[]>(cb: T) {
  const getter = useRecentGetter(cb);
  return [useCallback((...args: K) => getter()(...args), []), getter] as const;
}

type SliceFirstParamter<F> = F extends (a: any, ...args: infer U) => any
  ? (...args: U) => ReturnType<F>
  : never;

/**
 * Create a never changing callback that injects the latest given
 * data for the
 * @param args The deps to inject
 * @param cb The callback to inject into
 */
export function useRecentInjector<T extends any, K extends (init: T, ...args: any[]) => any>(
  args: T,
  cb: K
): SliceFirstParamter<K> {
  const inject = useRecentGetter(args);
  return useCallback((...args: any[]) => cb(inject(), ...args), []) as any;
}

export function useToggle<T extends unknown = boolean>(
  def: T,
  trueValue = true as T,
  falseValue = false as T
) {
  const [value, setTrue, setFalse, setValue] = useBoolean(def, trueValue, falseValue);
  const toggle: Toggle = useCallback(
    () => setValue(v => (v !== trueValue ? trueValue : falseValue)),
    [setValue]
  );

  return [value, toggle, setTrue, setFalse, setValue] as const;
}

type RequestEvents<ErrorResponse extends unknown, SuccessResponse> = {
  onFinish?: () => any;
  onError?: (e: ErrorResponse) => any;
  onSuccess?: (s: SuccessResponse) => any;
};

export function useRequest<SuccessResponse, ErrorResponse extends unknown = string>(
  requestFactory: () => Observable<SuccessResponse>,
  dependencies: any[] = null,
  {
    onFinish = null,
    onError = null,
    onSuccess = null,
  }: RequestEvents<ErrorResponse, SuccessResponse> = {},
  defaultError = '' as ErrorResponse,
  defaultLoading = true
) {
  const [loading, setLoading] = useState<boolean>(defaultLoading);
  const [error, setError] = useState<ErrorResponse>(defaultError);
  const [result, setResult] = useState<SuccessResponse>();
  useEffect(() => {
    const observable = requestFactory();
    if (observable) {
      setLoading(true);
      const sub = observable.subscribe({
        next: val => {
          setResult(val);
          onSuccess && onSuccess(val);
        },
        error: val => {
          setError(val);
          onError && onError(val);
        },
        complete: () => {
          setLoading(false);
          onFinish && onFinish();
        },
      });
      return () => sub.unsubscribe();
    }
  }, dependencies);
  return { loading, error, result };
}

export function useTrigger<
  SuccessResponse,
  RequestArgs extends any[],
  ErrorResponse extends unknown = string
>(
  requestFactory: (...args: RequestArgs) => Observable<SuccessResponse>,
  dependencies: any[] = null,
  {
    onFinish = null,
    onError = null,
    onSuccess = null,
  }: RequestEvents<ErrorResponse, SuccessResponse> = {},
  defaultError = '' as ErrorResponse,
  defaultLoading = true
) {
  const [loading, setLoading] = useState<boolean>(defaultLoading);
  const [error, setError] = useState<ErrorResponse>(defaultError);
  const [result, setResult] = useState<SuccessResponse>();
  const trigger = useCallback((...args: RequestArgs) => {
    const observable = requestFactory(...args);
    if (observable) {
      setLoading(true);
      setError(defaultError);
      observable.subscribe({
        next: val => {
          setResult(val);
          onSuccess && onSuccess(val);
          // setLoading(false);
        },
        error: val => {
          setError(val);
          onError && onError(val);
          setLoading(false);
          onFinish && onFinish();
        },
        complete: () => {
          setLoading(false);
          onFinish && onFinish();
        },
      });
    }
  }, dependencies);
  return { loading, error, result, trigger };
}

export const useMountedGetter = () => {
  const mountedRef = useRef<boolean>(false);

  // Basically the same as "useDidMount" because it has no dependencies
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      // The cleanup function of useEffect is called by React on unmount
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => mountedRef.current, []);
};

enum RequestTrigger {
  OFF = 0,
  ON_1 = 1,
  ON_2 = 2,
}

class RequestEvent<RequesterArgs extends any[]> {
  /**
   * Use the Event as a hook.
   */
  static useTrigger<A extends any[]>() {
    return useMemo(() => new this<A>(), []);
  }

  /**
   * Decider to fire the request.
   * @internal
   */
  protected run = RequestTrigger.OFF;
  /**
   * The args to execute the request event with.
   */
  protected args = [] as RequesterArgs;

  /**
   * Everytime this function is called, the request if fired. If there
   * are any on-going requests, those are canceled as part of the implementation.
   * @param args The args passedon to the request factory.
   */
  fire = (...args: RequesterArgs) => {
    this.args = args || ([] as RequesterArgs);
    // Flip the execution switch.
    this.run = this.run === RequestTrigger.ON_1 ? RequestTrigger.ON_2 : RequestTrigger.ON_1;
  };
}

/**
 * Create a trigger function for the given request.
 * @param requestFactory The function that creates the request.
 * @param dependencies The dependencies of the request
 */
export function useTriggerableRequest<
  SuccessResponse,
  RequestArgs extends any[],
  ErrorResponse extends any = string
>(
  requestFactory: (...args: RequestArgs) => Observable<SuccessResponse>,
  events: RequestEvents<ErrorResponse, SuccessResponse> = {},
  dependencies: any[] = [],
  defaultError = '' as ErrorResponse
) {
  const trigger = RequestEvent.useTrigger<RequestArgs>();

  const request = useRequest(
    () => trigger['run'] && requestFactory(...trigger['args']),
    [trigger['run'], ...dependencies],
    events,
    defaultError,
    false
  );

  return [trigger.fire, request] as const;
}

/**
 * Create an observable trigger of a api request or an observable.
 * @param requestFactory The request factory.
 * @param dependencies The dependencies of updating.
 */
export function useObservableTrigger<SuccessResponse, TriggerArgs extends any[]>(
  requestFactory: (...args: TriggerArgs) => Observable<SuccessResponse>,
  dependencies: any[] = []
) {
  const fire = useCallback((...args: TriggerArgs) => {
    return requestFactory(...args);
  }, dependencies);

  return fire;
}

/**
 * Get a flag that is momentairly set to false. This flag can be use to
 * force unmount and mount of a component.
 * @param deps The dependencies to remount.
 */
export function useForceReMount(deps?: any[]) {
  const [mount, setMount] = useState(true);
  useEffect(() => setMount(() => true), [mount]);
  useEffect(() => setMount(() => false), deps);
  return mount;
}

/**
 * Simple hook that subscribes to a factory returning observable. This can be used
 * with a factory that caches its responses. Add this to your parent page components
 * to preload any reusable responses.
 * @param factory The factory returning the observable.
 */
export function usePreload<T extends Observable<any>>(factory: () => T) {
  useEffect(() => {
    const sub = factory().subscribe();
    return () => sub && sub.unsubscribe();
  }, []);
}

/**
 * Cancellable request trigger(deal search and company search usage)
 * @param requestFactory
 * @param dependencies
 * @param param2
 * @param defaultError
 * @param defaultLoading
 */
export function useAtomicTrigger<
  SuccessResponse,
  RequestArgs extends any[],
  ErrorResponse extends unknown = string
>(
  requestFactory: (...args: RequestArgs) => Observable<SuccessResponse>,
  dependencies: any[] = null,
  {
    onFinish = null,
    onError = null,
    onSuccess = null,
  }: RequestEvents<ErrorResponse, SuccessResponse> = {},
  defaultError = '' as ErrorResponse,
  defaultLoading = true
) {
  const [loading, setLoading] = useState<boolean>(defaultLoading);
  const [error, setError] = useState<ErrorResponse>(defaultError);
  const [result, setResult] = useState<SuccessResponse>();
  const subscription = useRef<Subscription>(null);
  const trigger = useCallback((...args: RequestArgs) => {
    const observable = requestFactory(...args);
    if (observable) {
      setLoading(true);
      setError(defaultError);
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
      subscription.current = observable.subscribe({
        next: val => {
          setResult(val);
          onSuccess && onSuccess(val);
          // setLoading(false);
        },
        error: val => {
          setError(val);
          onError && onError(val);
          setLoading(false);
          onFinish && onFinish();
        },
        complete: () => {
          setLoading(false);
          onFinish && onFinish();
        },
      });
    }
  }, dependencies);
  return { loading, error, result, trigger };
}

export function useDebouncedCallback(callback, delay) {
  const callbackRef = useRef(null);
  callbackRef.current = callback;
  return useCallback(
    debounce((...args) => callbackRef.current(...args), delay),
    []
  );
}
